/* eslint-disable no-shadow */
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import NotificationServiceClient from '@/api/NotificationServiceClient';
import Analytics from '@/mixins/Analytics';
import eventNames from '../../constants/analyticsEventNames';
import router from '../../router';

export const defaultState = {
  config: {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  },
  deviceToken: null,
  userId: null,
};

export const actions = {
  async initFirebase({
    commit, state, dispatch, getters,
  }) {
    const firebaseCompatible = 'Notification' in window && 'serviceWorker' in navigator;
    if (!firebaseCompatible) return;

    const app = initializeApp(state.config);
    const fcm = getMessaging(app);
    let currentToken;
    const { permission } = Notification;
    if (permission !== 'granted') return;

    try {
      const swRegistration = await navigator.serviceWorker.getRegistration();
      currentToken = await getToken(fcm, {
        vapidKey: import.meta.env.VITE_FCM_VAPID_KEY,
        serviceWorkerRegistration: swRegistration,
      });

      if (currentToken) {
        const notificationClient = new NotificationServiceClient();
        await notificationClient.setDeviceToken(currentToken, state.deviceToken);
        commit('setRegistration', { token: currentToken, userId: getters.currentUser.id });
        onMessage(fcm, (payload) => {
          dispatch('handleFcmMessage', payload);
        });

        navigator.serviceWorker.onmessage = (message) => {
          if (message?.data.type === 'notificationclick') {
            dispatch('handleNotificationClick', message);
          }
        };
      }
    } catch (err) {
      Analytics.methods.noticeError(err, {
        currentToken,
        permission,
      });
    }
  },
  handleNotificationClick({ rootState }, message) {
    const { data } = message;
    const trackingEnabled = import.meta.env.VITE_ENV_NAME !== 'development' || rootState.devTools.analyticsLoggingEnabled;
    if (data?.acv_message_type && trackingEnabled) {
      Analytics.methods.track(eventNames.notifications.pushNotificationClicked, {
        auction_id: data.auction_id,
        acv_message_type: data.acv_message_type,
        lane_id: data.private_lane_id,
      });
    }

    if (data?.link) {
      router.push(data.link);
    }
  },
  async handleFcmMessage({
    getters, dispatch, rootState,
  }, payload) {
    const data = payload?.data;
    const { l: lane } = rootState.route.query;
    if (data?.auction_id) {
      const isScheduledLaunchNotification = ['PRE_LAUNCH', 'LAUNCH_CANCELLED'].includes(data?.acv_message_type);
      const isLiveLaunchNotification = data?.acv_message_type === 'LAUNCH_NOTIFICATION_V2' && getters.shouldAddAuctionToList && lane === 'live';
      if (isScheduledLaunchNotification || isLiveLaunchNotification) {
        const auctionId = parseInt(data.auction_id, 10);
        const fetchMethod = rootState.marketplaceAuctions?.selectedAuctionLane?.fetchMethod;
        await dispatch(
          'marketplaceAuctions/getMarketplaceAuctionsByClientMethod',
          {
            fetchMethod,
            params: [
              [{
                filterType: 'id',
                value: auctionId,
              }],
            ],
          },
        );
      }
    }

    dispatch('sendNotification', data);
  },
  sendNotification(context, data) {
    if ('Notification' in window && 'serviceWorker' in navigator) {
      const options = {
        body: data.title,
        icon: data.image,
        data,
      };

      const showNotification = async () => {
        const swRegistration = await navigator.serviceWorker.getRegistration();
        if (swRegistration) {
          swRegistration.showNotification(data.body, options);
        }
      };

      if (Notification.permission === 'granted') {
        showNotification();
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then((permission) => {
          if (permission === 'granted') {
            showNotification();
          }
        });
      }
    }
  },
  deleteFcmRegistration({ state, commit }) {
    const notificationClient = new NotificationServiceClient();
    notificationClient.deleteDeviceToken(state.deviceToken);
    commit('deleteRegistration');
  },
};

export const mutations = {
  setRegistration(state, { token, userId }) {
    state.deviceToken = token;
    state.userId = userId;
  },
  deleteRegistration(state) {
    state.deviceToken = null;
    state.userId = null;
  },
};

export const getters = {
  atEndOfActiveList: (state, getters, rootState) => !rootState.marketplaceAuctions.hasMorePages,
  auctionBelongsOnActiveList: (state, getters, rootState) => ['Search', 'Marketplace'].includes(rootState.route.name),
  /* eslint-disable-next-line vue/max-len */
  userHasSearchActive: (state, getters, rootState) => rootState.marketplaceAuctions.adHocSearchIsActive
      || rootState.marketplaceAuctions.savedSearchIsActive
      || getters['marketplaceAuctions/hasSelectedAuctionLaneSubFilter'],
  shouldAddAuctionToList: (state, getters, rootState) => {
    const currentTourPath = rootState.tours?.currentTourPath;
    if (currentTourPath || getters.userHasSearchActive) {
      return false;
    }

    return getters.atEndOfActiveList && getters.auctionBelongsOnActiveList;
  },
};

const firebase = {
  state: defaultState,
  mutations,
  actions,
  getters,
};

export default firebase;
